import { getFunctions, httpsCallable } from 'firebase/functions';
import type { PartnerToken, BusinessVisibility } from '@/types';

const functions = getFunctions();

// ===========================================
// Types
// ===========================================

interface GenerateTokenInput {
  companyId: string;
  companyName: string;
  businessVisibility: BusinessVisibility;
}

interface GenerateTokenOutput {
  success: boolean;
  token?: string;
  tokenId?: string;
  expiresAt?: string;
  magicLink?: string;
  error?: string;
}

interface ListTokensInput {
  companyId?: string;
  includeRevoked?: boolean;
}

interface ListTokensOutput {
  success: boolean;
  tokens?: PartnerToken[];
  error?: string;
}

interface RevokeTokenInput {
  tokenId: string;
}

interface RevokeTokenOutput {
  success: boolean;
  error?: string;
}

interface ResendInviteInput {
  tokenId: string;
  contactEmail: string;
  contactName: string;
}

interface ResendInviteOutput {
  success: boolean;
  message?: string;
  error?: string;
}

// ===========================================
// API Functions
// ===========================================

/**
 * Generate a new partner access token (active until revoked)
 */
export async function generatePartnerToken(
  companyId: string,
  companyName: string,
  businessVisibility: BusinessVisibility
): Promise<GenerateTokenOutput> {
  const generate = httpsCallable<GenerateTokenInput, GenerateTokenOutput>(
    functions,
    'crm_generatePartnerToken'
  );

  const result = await generate({
    companyId,
    companyName,
    businessVisibility,
  });

  return result.data;
}

/**
 * List partner tokens, optionally filtered by company
 */
export async function listPartnerTokens(
  companyId?: string,
  includeRevoked: boolean = false
): Promise<ListTokensOutput> {
  const list = httpsCallable<ListTokensInput, ListTokensOutput>(
    functions,
    'crm_listPartnerTokens'
  );

  const result = await list({
    companyId,
    includeRevoked,
  });

  return result.data;
}

/**
 * Revoke a partner token
 */
export async function revokePartnerToken(tokenId: string): Promise<RevokeTokenOutput> {
  const revoke = httpsCallable<RevokeTokenInput, RevokeTokenOutput>(
    functions,
    'crm_revokePartnerToken'
  );

  const result = await revoke({ tokenId });
  return result.data;
}

interface DeleteTokenInput {
  tokenId: string;
}

interface DeleteTokenOutput {
  success: boolean;
  error?: string;
}

/**
 * Permanently delete a revoked partner token
 * Only works for tokens that have been revoked
 */
export async function deletePartnerToken(tokenId: string): Promise<DeleteTokenOutput> {
  const deleteToken = httpsCallable<DeleteTokenInput, DeleteTokenOutput>(
    functions,
    'crm_deletePartnerToken'
  );

  const result = await deleteToken({ tokenId });
  return result.data;
}

/**
 * Resend partner invite email using an existing token
 * Does NOT create a new token - uses the existing one
 */
export async function resendPartnerInvite(
  tokenId: string,
  contactEmail: string,
  contactName: string
): Promise<ResendInviteOutput> {
  console.log('[resendPartnerInvite] Starting with:', { tokenId, contactEmail, contactName });

  const resend = httpsCallable<ResendInviteInput, ResendInviteOutput>(
    functions,
    'crm_resendPartnerInvite'
  );

  console.log('[resendPartnerInvite] Calling Cloud Function crm_resendPartnerInvite...');

  try {
    const result = await resend({
      tokenId,
      contactEmail,
      contactName,
    });

    console.log('[resendPartnerInvite] Cloud Function response:', result.data);
    return result.data;
  } catch (error: any) {
    console.error('[resendPartnerInvite] Cloud Function error:', {
      code: error?.code,
      message: error?.message,
      details: error?.details,
      stack: error?.stack,
    });
    throw error;
  }
}

/**
 * Generate the magic link URL for a token
 */
export function getMagicLinkUrl(token: string): string {
  // Always use production URL for partner portal links
  const baseUrl = 'https://crm.azdisaster.com';
  return `${baseUrl}/partner/login?token=${encodeURIComponent(token)}`;
}
